/*
  <pdp-carousel> — the carousel for the rebuilt PDP sections.

  Replaces assets/replo-carousel.js, which drove arrows only and was wired into one section, so
  73 dot controls and 28 arrows across the converted pages did nothing at all.

  ── Why scroll-snap rather than a transform ──────────────────────────────────────────
  Replo paged by writing `transform: translate3d(...)` on the track from its runtime. That is
  why the conversion froze: the baked HTML kept whatever offset the track happened to have when
  it was captured, and with the runtime stripped nothing ever changed it again.

  Here the track is a scroll container with `scroll-snap-type: x mandatory`, so the BROWSER owns
  the position. That means drag, swipe, momentum, trackpad, shift-wheel, keyboard scrolling and
  reduced-motion all work before any JavaScript runs, and there is no state to freeze. This file
  only reflects the scroll position into the dots, the arrows' disabled state and a live region —
  it never sets the position except in response to a click.

  Contract (all optional except the track):
    [data-pdp-carousel-track]   the scroll container; its element children are the slides
    loop                        wrap past either end instead of stopping (opt-in)
    [data-pdp-carousel-prev]    previous button
    [data-pdp-carousel-next]    next button
    [data-pdp-carousel-dot]     one per slide, in order
    [data-pdp-carousel-status]  visually-hidden element for "Slide 1 of 3"

  The active dot gets `aria-current="true"` and `.is-active`.
*/

class PdpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-pdp-carousel-track]');
    if (!this.track) return;

    this.slides = [...this.track.children];
    this.dots = [...this.querySelectorAll('[data-pdp-carousel-dot]')];
    this.prev = this.querySelector('[data-pdp-carousel-prev]');
    this.next = this.querySelector('[data-pdp-carousel-next]');
    this.status = this.querySelector('[data-pdp-carousel-status]');
    this.index = 0;

    this.prev?.addEventListener('click', () => this.go(this.index - 1));
    this.next?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));

    // Arrow keys on the container, for parity with a native listbox. The track is also
    // focusable and scrollable, so this is an addition rather than the only route in.
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { this.go(this.index - 1); e.preventDefault(); }
      if (e.key === 'ArrowRight') { this.go(this.index + 1); e.preventDefault(); }
    });

    // rAF-coalesced: a snap scroll fires dozens of events and each sync touches the DOM.
    let queued = false;
    this.track.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; this.sync(); });
    }, { passive: true });

    // A resize changes the step, and on a percentage-width track it can change which slide is
    // nearest, so the readout has to be recomputed rather than trusted.
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => this.sync()).observe(this.track);
    }

    this.sync();
  }

  get step() {
    // Measured from the first two slides rather than assumed, so a gap or a partial-slide layout
    // still pages by exactly one slide. Falls back to the viewport width for a single slide.
    if (this.slides.length > 1) {
      return this.slides[1].getBoundingClientRect().left - this.slides[0].getBoundingClientRect().left;
    }
    return this.track.clientWidth;
  }

  get loops() {
    return this.hasAttribute('loop');
  }

  go(i) {
    const max = this.track.scrollWidth - this.track.clientWidth;

    // Wrapping has to be decided on the SCROLL RANGE, not the index. `step` is one viewport, and
    // a viewport can hold several slides — so `lastIndex * step` overshoots `max`, the browser
    // clamps it, and wrapping "to the last slide" silently lands in the middle of the track.
    // Asking whether we are already parked at an end is independent of how many slides fit.
    if (this.loops) {
      const back = i < this.index;
      if (back && this.track.scrollLeft <= 1) {
        this.track.scrollTo({ left: max, behavior: 'auto' });
        return;
      }
      if (!back && this.track.scrollLeft >= max - 1) {
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }
    }

    const clamped = Math.max(0, Math.min(i, this.slides.length - 1));
    this.track.scrollTo({ left: clamped * this.step, behavior: 'smooth' });
    // Do not set this.index here — let the scroll handler derive it, so the readout can never
    // disagree with what is actually on screen (a cancelled or clamped scroll included).
  }

  sync() {
    const step = this.step;
    const i = step > 0
      ? Math.max(0, Math.min(Math.round(this.track.scrollLeft / step), this.slides.length - 1))
      : 0;
    if (i !== this.index) this.index = i;

    this.dots.forEach((dot, n) => {
      dot.classList.toggle('is-active', n === i);
      if (n === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    // `scrollWidth - clientWidth` rather than the index, because a track whose last slide is
    // partially visible reaches its scroll end before the last index.
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1;
    if (this.prev) this.prev.disabled = this.loops ? false : atStart;
    if (this.next) this.next.disabled = this.loops ? false : atEnd;

    if (this.status) {
      const text = `Slide ${i + 1} of ${this.slides.length}`;
      if (this.status.textContent !== text) this.status.textContent = text;
    }
  }
}

if (!customElements.get('pdp-carousel')) {
  customElements.define('pdp-carousel', PdpCarousel);
}
